import { isDefined } from "@omni/utils/helper.utils";
import { ComponentType, createContext, ReactNode, use } from "react";

type MappedContextValue = {
    index: number;
};

type MappedContextProviderProps = {
    index?: number;
    children: ReactNode;
};

type Props<T> = {
    array: Array<T>;
    Wrapper?: ComponentType<{ item?: T; children: ReactNode }>;
    children: ReactNode;
};

const MappedContext = createContext<MappedContextValue | undefined>(undefined);

export const MappedContextProvider = ({
    index,
    children
}: MappedContextProviderProps) => {
    if (!isDefined(index)) return null;

    return <MappedContext value={{ index: index }}>{children}</MappedContext>;
};

export const Mapped = <T,>({ array, Wrapper, children }: Props<T>) => {
    return array.map((_, index) => (
        <MappedContextProvider
            key={index}
            index={index}
        >
            {Wrapper && <Wrapper item={array[index]}>{children}</Wrapper>}
            {!Wrapper && children}
        </MappedContextProvider>
    ));
};

export const useMapped = (fallback?: number) => {
    const context = use<MappedContextValue | undefined>(MappedContext);

    const index = context?.index ?? fallback;

    if (!isDefined(index)) {
        throw new Error();
    }

    return { index: index };
};
